import { action, makeObservable, observable } from 'mobx';
import { Injectable } from './service.base';
import { AxiosResponse } from 'axios';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import type { IRedeemAmount, IRedeemLocation } from './service.types';
import { extractServerError } from '~/tools/error';
import { WrapperService } from '~/services/service.wrapper';
import type { AuthService } from '~/services/service.auth';

@Injectable()
export class RedeemDialogService {
    @observable public open = false;
    @observable public showStep1 = true;
    @observable public showConfirmStep = false;
    @observable public showSuccessStep = false;

    @observable public slideArrowLeftEnabled = false;
    @observable public slideArrowRightEnabled = true;
    @observable public showLoading = false;

    @observable public selectedLocation = '0';
    @observable public redeemAmounts: Array<IRedeemAmount> = [];
    @observable public redeemLocations: Array<IRedeemLocation> = [];
    @observable public lastError: { description: string; code?: string } = { description: '', code: undefined };

    @observable public selectedRedeem: IRedeemAmount | null = null;
    @observable public selectedLocationName = '';
    @observable public confirmedAmount = 0;
    @observable public confirmedLocationName = '';

    private authService: AuthService;

    constructor(wrapperService: WrapperService) {
        this.authService = wrapperService.authService;
        makeObservable(this);
        const storage = window.sessionStorage.getItem('redeemDialog');
        if (storage) {
            this.openFromStorage(storage);
        }
    }

    @action
    handleOpen = async (authService: AuthService) => {
        this.authService = authService;
        this.resetDialog();
        await this.getRedeemOptions(this.authService);
        this.persistSession();
        this.setOpen(true);
    };

    @action
    setOpen = (value: boolean) => {
        this.open = value;
    };

    @action
    handleClose = () => {
        this.open = false;
        this.resetDialog();
        window.sessionStorage.removeItem('redeemDialog');
    };

    @action
    handleContinueToConfirm = () => {
        if (!this.selectedRedeem || this.selectedLocation === '0') {
            return;
        }

        this.selectedLocationName = this.getSelectedLocation();
        this.showStep1 = false;
        this.showConfirmStep = true;
        this.showSuccessStep = false;
        this.persistSession();
    };

    @action
    handleBackToStep1 = () => {
        this.showStep1 = true;
        this.showConfirmStep = false;
        this.showSuccessStep = false;
        this.persistSession();
    };

    @action
    handleConfirmRedemption = async (alertDialogSrv: any) => {
        const user = this.authService.currentUser;
        if (!user) {
            return;
        }

        try {
            this.setShowLoading(true);
            this.clearLastError();

            const selectedRedeemValue = this.selectedRedeem ? this.selectedRedeem.value : 0;
            const response: AxiosResponse = await api.post(
                EApiUrl.redeem,
                { amount: selectedRedeemValue, locationCode: this.selectedLocation },
                { headers: { Authorization: user.token } },
            );

            this.setShowLoading(false);
            if (response.status === 200) {
                this.confirmedAmount = selectedRedeemValue;
                this.confirmedLocationName = this.getSelectedLocation();
                user.freePlay = Math.max(0, Math.floor(+user.freePlay - selectedRedeemValue));

                this.showStep1 = false;
                this.showConfirmStep = false;
                this.showSuccessStep = true;
                this.getRedeemOptions(this.authService);
                void this.authService.getUserBalance();
                this.setSelectedLocation('0');
                this.persistSession();
            }
        } catch (error: any) {
            this.setShowLoading(false);
            if (error.response && error.response.status && error.response.status === 403) {
                this.authService.setLastError(extractServerError(error?.response?.data ?? error));
                this.authService.setShowForbiddenPopup(true);
            } else {
                this.setLastError(extractServerError(error?.response?.data ?? error));
                alertDialogSrv.handleOpen();
            }
        }
    };

    @action
    setShowLoading = (value: boolean) => {
        this.showLoading = value;
    };

    @action
    handleLocationChange = (event: { target: { value: string } }) => {
        this.setSelectedLocation(event.target.value);
        this.persistSession();
    };

    @action
    setRedeemAmounts = (redeemAmounts: Array<IRedeemAmount>) => {
        this.redeemAmounts = redeemAmounts;
    };

    @action
    setRedeemLocations = (redeemLocations: Array<IRedeemLocation>) => {
        this.redeemLocations = redeemLocations;
    };

    @action
    handleSlideChange = (swiper: any) => {
        if (swiper.isBeginning) {
            this.slideArrowLeftEnabled = false;
        } else {
            this.slideArrowLeftEnabled = true;
        }

        if (swiper.isEnd) {
            this.slideArrowRightEnabled = false;
        } else {
            this.slideArrowRightEnabled = true;
        }
    };

    @action
    handleSlideClick = (redeemAmount: IRedeemAmount) => {
        if (!redeemAmount.disabled) {
            const currentActive: IRedeemAmount | undefined = this.redeemAmounts.find(item => item.active === true);
            if (currentActive) {
                currentActive.active = false;
            }

            this.setSelectedRedeem(redeemAmount);
            redeemAmount.active = true;
            this.persistSession();
        }
    };

    @action
    getSelectedLocation = (): string => {
        const selectedLocation = this.redeemLocations.find(item => item.code === this.selectedLocation);

        return selectedLocation ? selectedLocation.name : '';
    };

    @action
    onRedeemKeyUp = (e: any, alertDialogSrv: any) => {
        if (!this.open || e.keyCode !== 13) {
            return;
        }

        if (this.showStep1 && this.selectedRedeem && this.selectedLocation !== '0') {
            this.handleContinueToConfirm();
        } else if (this.showConfirmStep && !this.showLoading) {
            this.handleConfirmRedemption(alertDialogSrv);
        }
    };

    @action
    setSelectedRedeem = (redeemAmount: IRedeemAmount | null) => {
        this.selectedRedeem = redeemAmount;
    };

    @action
    setSelectedLocation = (selectedLocation: string) => {
        this.selectedLocation = selectedLocation;
    };

    persistSession = () => {
        window.sessionStorage.setItem(
            'redeemDialog',
            JSON.stringify({
                selectedRedeem: this.selectedRedeem,
                selectedLocation: this.selectedLocation,
                selectedLocationName: this.selectedLocationName,
                showConfirmStep: this.showConfirmStep,
                showSuccessStep: this.showSuccessStep,
                confirmedAmount: this.confirmedAmount,
                confirmedLocationName: this.confirmedLocationName,
            }),
        );
    };

    @action
    openFromStorage = async (storage: string) => {
        const data = JSON.parse(storage);
        if (!data?.showConfirmStep && !data?.showSuccessStep && !data?.selectedRedeem) {
            return;
        }

        this.open = true;
        if (data.selectedRedeem) {
            this.setSelectedRedeem(data.selectedRedeem);
        }
        if (data.selectedLocation) {
            this.setSelectedLocation(data.selectedLocation);
        }
        if (data.selectedLocationName) {
            this.selectedLocationName = data.selectedLocationName;
        }

        if (data.showSuccessStep) {
            this.showStep1 = false;
            this.showConfirmStep = false;
            this.showSuccessStep = true;
            this.confirmedAmount = data.confirmedAmount ?? 0;
            this.confirmedLocationName = data.confirmedLocationName ?? '';
        } else if (data.showConfirmStep) {
            this.showStep1 = false;
            this.showConfirmStep = true;
        }

        for (let i = 0; i < 100 && !this.authService.currentUser?.token; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        await this.getRedeemOptions(this.authService);

        if (data.selectedRedeem && this.showStep1) {
            const redeem = this.redeemAmounts.find(item => item.value === this.selectedRedeem?.value);
            if (redeem) {
                redeem.active = true;
            }
        } else if (this.showConfirmStep && !this.selectedLocationName) {
            this.selectedLocationName = this.getSelectedLocation();
        }
    };

    @action
    getRedeemOptions = async (authService: AuthService) => {
        if (authService && authService.currentUser) {
            try {
                const response: AxiosResponse = await api.get(EApiUrl.redeemOptions, {
                    headers: {
                        Authorization: authService.currentUser.token,
                    },
                });
                if (response.status === 200) {
                    const selectableSet = new Set(response.data?.selectable.map(String));
                    const redeemAmounts: Array<IRedeemAmount> = [];

                    for (const item of response.data?.amounts) {
                        redeemAmounts.push({
                            value: +item,
                            disabled: !selectableSet.has(String(item)),
                            active: false,
                        });
                    }

                    this.setRedeemAmounts(redeemAmounts);

                    const locations: Array<IRedeemLocation> = [];
                    for (const item of response.data?.locations) {
                        locations.push({
                            code: item.code,
                            name: item.display_name,
                        });
                    }
                    this.setRedeemLocations(locations);
                }
            } catch (error: any) {
                if (error.response && error.response.status && error.response.status === 403) {
                    this.authService.setLastError(extractServerError(error?.response?.data ?? error));
                    authService.setShowForbiddenPopup(true);
                } else {
                    this.setLastError(extractServerError(error?.response?.data ?? error));
                }
            }
        }
    };

    @action
    setLastError = (err: { description: string; code?: string }) => {
        this.lastError = err;
    };

    @action
    clearLastError = () => {
        this.lastError = { description: '', code: undefined };
    };

    private resetDialog = () => {
        this.setSelectedRedeem(null);
        this.setSelectedLocation('0');
        this.selectedLocationName = '';
        this.confirmedAmount = 0;
        this.confirmedLocationName = '';
        this.showStep1 = true;
        this.showConfirmStep = false;
        this.showSuccessStep = false;
    };
}
